import type { Asset } from '../types/domain';
import { formatDayKey } from '../utils/assetMedia';
import { isHomeDiaryAsset } from '../utils/subjectConfig';
import { assetService } from './assetService';

const DAY_INDEX_TTL_MS = 3 * 60 * 1000;
const RECENT_DAY_WINDOW = 3;
const DAY_PAGE_SIZE = 50;
const PAS_PRELOAD_DAY_WINDOW = 3;
/** Bump when list asset payload shape changes (e.g. ai_title projection). */
const HOME_ASSET_DAY_PAYLOAD_VERSION = 6;

export type AssetDayTypeFilter = 'pas' | 'exclude_pas';

export type AssetDayIndexEntry = {
  dayKey: string;
  count: number;
};

export type HomeAssetDayStoreSnapshot = {
  days: AssetDayIndexEntry[];
  assetsByDay: Record<string, Asset[]>;
  expandedDayKeys: string[];
  loadingDayKeys: string[];
  dayIndexFetchedAt: number;
};

type HomeAssetDayBucket = {
  assets: Asset[];
  loadedAt: number;
};

type HomeAssetDayStoreState = {
  payloadVersion: number;
  days: AssetDayIndexEntry[];
  dayIndexFetchedAt: number;
  dayIndexSignature: string;
  assetsByDay: Map<string, HomeAssetDayBucket>;
  expandedDayKeys: Set<string>;
  loadingDayKeys: Set<string>;
};

const storesByKey = new Map<string, HomeAssetDayStoreState>();

function buildStoreKey(
  userId: string,
  subject: string,
  typeFilter: AssetDayTypeFilter = 'exclude_pas',
) {
  return `${String(userId || '').trim()}:${String(subject || '').trim()}:${typeFilter}`;
}

function createEmptyStore(): HomeAssetDayStoreState {
  return {
    payloadVersion: HOME_ASSET_DAY_PAYLOAD_VERSION,
    days: [],
    dayIndexFetchedAt: 0,
    dayIndexSignature: '',
    assetsByDay: new Map(),
    expandedDayKeys: new Set(),
    loadingDayKeys: new Set(),
  };
}

function getOrCreateStore(
  userId: string,
  subject: string,
  typeFilter: AssetDayTypeFilter = 'exclude_pas',
) {
  const key = buildStoreKey(userId, subject, typeFilter);
  let store = storesByKey.get(key);
  if (!store) {
    store = createEmptyStore();
    storesByKey.set(key, store);
  } else if (store.payloadVersion !== HOME_ASSET_DAY_PAYLOAD_VERSION) {
    store.payloadVersion = HOME_ASSET_DAY_PAYLOAD_VERSION;
    store.assetsByDay.clear();
    store.dayIndexFetchedAt = 0;
    store.dayIndexSignature = '';
  }
  return store;
}

function buildDayIndexSignature(days: AssetDayIndexEntry[]) {
  return days.map((entry) => `${entry.dayKey}:${entry.count}`).join('|');
}

function parseDayKeyDate(dayKey: string) {
  return new Date(`${dayKey}T00:00:00`);
}

function shiftDayKey(dayKey: string, deltaDays: number) {
  const date = parseDayKeyDate(dayKey);
  date.setDate(date.getDate() + deltaDays);
  return formatDayKey(date);
}

export function getRecentDayKeys(todayKey: string = formatDayKey(new Date()), windowSize: number = RECENT_DAY_WINDOW) {
  return Array.from({ length: Math.max(1, windowSize) }, (_, index) => shiftDayKey(todayKey, -index));
}

/** 首页默认展开：按日索引取最近 N 个「有内容」的日期（非日历连续日）。 */
export function getRecentAvailableDayKeys(
  days: AssetDayIndexEntry[],
  windowSize: number = RECENT_DAY_WINDOW,
): string[] {
  const limit = Math.max(1, windowSize);
  return (days || [])
    .filter((entry) => entry.count > 0)
    .slice(0, limit)
    .map((entry) => entry.dayKey);
}

function isRecentDayKey(dayKey: string, todayKey: string) {
  return getRecentDayKeys(todayKey).includes(dayKey);
}

function mergeAssetsById(primary: Asset[], secondary: Asset[]) {
  const map = new Map<string, Asset>();
  for (const asset of [...secondary, ...primary]) {
    if (!asset?.id) continue;
    map.set(asset.id, asset);
  }
  return Array.from(map.values()).sort((left, right) => {
    const leftAt = new Date(left.created_at).getTime();
    const rightAt = new Date(right.created_at).getTime();
    if (rightAt !== leftAt) return rightAt - leftAt;
    return String(right.id).localeCompare(String(left.id));
  });
}

function snapshotStore(store: HomeAssetDayStoreState): HomeAssetDayStoreSnapshot {
  const assetsByDay: Record<string, Asset[]> = {};
  for (const [dayKey, bucket] of store.assetsByDay.entries()) {
    assetsByDay[dayKey] = bucket.assets.slice();
  }
  return {
    days: store.days.map((entry) => ({ ...entry })),
    assetsByDay,
    expandedDayKeys: Array.from(store.expandedDayKeys),
    loadingDayKeys: Array.from(store.loadingDayKeys),
    dayIndexFetchedAt: store.dayIndexFetchedAt,
  };
}

export function readHomeAssetDayStore(
  userId: string,
  subject: string,
  typeFilter: AssetDayTypeFilter = 'exclude_pas',
): HomeAssetDayStoreSnapshot {
  return snapshotStore(getOrCreateStore(userId, subject, typeFilter));
}

export function invalidateHomeAssetDayIndex(
  userId?: string,
  subject?: string,
  typeFilter?: AssetDayTypeFilter,
) {
  if (!userId) {
    for (const store of storesByKey.values()) {
      store.dayIndexFetchedAt = 0;
      store.dayIndexSignature = '';
    }
    return;
  }
  if (!subject) {
    const prefix = `${String(userId).trim()}:`;
    for (const [key, store] of storesByKey.entries()) {
      if (key.startsWith(prefix)) {
        store.dayIndexFetchedAt = 0;
        store.dayIndexSignature = '';
      }
    }
    return;
  }
  if (!typeFilter) {
    for (const candidate of ['exclude_pas', 'pas'] as const) {
      const store = storesByKey.get(buildStoreKey(userId, subject, candidate));
      if (store) {
        store.dayIndexFetchedAt = 0;
        store.dayIndexSignature = '';
      }
    }
    return;
  }
  const store = storesByKey.get(buildStoreKey(userId, subject, typeFilter));
  if (store) {
    store.dayIndexFetchedAt = 0;
    store.dayIndexSignature = '';
  }
}

async function fetchDayAssets(input: {
  userId: string;
  subject: string;
  dayKey: string;
  typeFilter?: AssetDayTypeFilter;
}) {
  const typeFilter = input.typeFilter === 'pas' ? 'pas' : 'exclude_pas';
  const assets = await assetService.listAssetsByDay({
    userId: input.userId,
    subject: input.subject,
    dayKey: input.dayKey,
    typeFilter,
    pageSize: DAY_PAGE_SIZE,
  });
  return typeFilter === 'pas' ? assets : assets.filter((asset) => isHomeDiaryAsset(asset));
}

export async function ensureHomeAssetDayLoaded(input: {
  userId: string;
  subject: string;
  dayKey: string;
  forceRefresh?: boolean;
  typeFilter?: AssetDayTypeFilter;
}): Promise<Asset[]> {
  const typeFilter = input.typeFilter === 'pas' ? 'pas' : 'exclude_pas';
  const store = getOrCreateStore(input.userId, input.subject, typeFilter);
  const dayKey = String(input.dayKey || '').trim();
  if (!dayKey) return [];

  store.expandedDayKeys.add(dayKey);
  const cached = store.assetsByDay.get(dayKey);
  if (cached && !input.forceRefresh) {
    return cached.assets.slice();
  }

  store.loadingDayKeys.add(dayKey);
  try {
    const assets = await fetchDayAssets({
      userId: input.userId,
      subject: input.subject,
      dayKey,
      typeFilter,
    });
    store.assetsByDay.set(dayKey, { assets, loadedAt: Date.now() });
    const existing = store.days.find((entry) => entry.dayKey === dayKey);
    if (existing) {
      existing.count = Math.max(existing.count, assets.length);
    } else if (assets.length > 0) {
      store.days = [{ dayKey, count: assets.length }, ...store.days]
        .sort((left, right) => right.dayKey.localeCompare(left.dayKey));
    }
    return assets.slice();
  } finally {
    store.loadingDayKeys.delete(dayKey);
  }
}

export function collapseHomeAssetDay(
  userId: string,
  subject: string,
  dayKey: string,
  typeFilter: AssetDayTypeFilter = 'exclude_pas',
) {
  const store = getOrCreateStore(userId, subject, typeFilter);
  store.expandedDayKeys.delete(dayKey);
}

export function expandHomeAssetDay(
  userId: string,
  subject: string,
  dayKey: string,
  typeFilter: AssetDayTypeFilter = 'exclude_pas',
) {
  getOrCreateStore(userId, subject, typeFilter).expandedDayKeys.add(dayKey);
}

export function mergeUploadedAssetIntoHomeStore(
  userId: string,
  subject: string,
  asset: Asset,
  typeFilter: AssetDayTypeFilter = 'exclude_pas',
) {
  if (typeFilter !== 'pas' && (!asset?.id || !isHomeDiaryAsset(asset))) return;
  if (!asset?.id) return;
  const store = getOrCreateStore(userId, subject, typeFilter);
  const dayKey = formatDayKey(asset.created_at || new Date());
  const existing = store.assetsByDay.get(dayKey)?.assets || [];
  store.assetsByDay.set(dayKey, {
    assets: mergeAssetsById([asset], existing),
    loadedAt: Date.now(),
  });
  store.expandedDayKeys.add(dayKey);
  const indexEntry = store.days.find((entry) => entry.dayKey === dayKey);
  if (indexEntry) {
    indexEntry.count = Math.max(indexEntry.count, store.assetsByDay.get(dayKey)?.assets.length || 0);
  } else {
    store.days = [{ dayKey, count: 1 }, ...store.days]
      .sort((left, right) => right.dayKey.localeCompare(left.dayKey));
  }
  store.dayIndexFetchedAt = 0;
  store.dayIndexSignature = '';
}

export function mergeHomeStoreAssetsByIds(
  userId: string,
  subject: string,
  assets: Asset[],
  typeFilter: AssetDayTypeFilter = 'exclude_pas',
) {
  if (!assets.length) return;
  const store = getOrCreateStore(userId, subject, typeFilter);
  const byDay = new Map<string, Asset[]>();
  for (const asset of assets) {
    if (!asset?.id) continue;
    const dayKey = formatDayKey(asset.created_at || new Date());
    const list = byDay.get(dayKey) || [];
    list.push(asset);
    byDay.set(dayKey, list);
  }
  for (const [dayKey, dayAssets] of byDay.entries()) {
    const existing = store.assetsByDay.get(dayKey)?.assets || [];
    store.assetsByDay.set(dayKey, {
      assets: mergeAssetsById(dayAssets, existing),
      loadedAt: Date.now(),
    });
    store.expandedDayKeys.add(dayKey);
  }
}

export async function syncHomeAssetDayStore(input: {
  userId: string;
  subject: string;
  forceRefresh?: boolean;
  supplementalAssets?: Asset[];
  typeFilter?: AssetDayTypeFilter;
}): Promise<HomeAssetDayStoreSnapshot> {
  const typeFilter = input.typeFilter === 'pas' ? 'pas' : 'exclude_pas';
  const store = getOrCreateStore(input.userId, input.subject, typeFilter);
  const todayKey = formatDayKey(new Date());
  const calendarRecentDayKeys = getRecentDayKeys(todayKey);
  const now = Date.now();
  const indexFresh = !input.forceRefresh
    && store.dayIndexFetchedAt > 0
    && now - store.dayIndexFetchedAt < DAY_INDEX_TTL_MS;

  let nextDays = store.days;

  if (!indexFresh) {
    const index = await assetService.listAssetDayIndex({
      userId: input.userId,
      subject: input.subject,
      typeFilter,
    });
    nextDays = index.days;
    const signature = buildDayIndexSignature(nextDays);
    const signatureChanged = signature !== store.dayIndexSignature && store.dayIndexSignature !== '';
    store.days = nextDays;
    store.dayIndexFetchedAt = now;
    store.dayIndexSignature = signature;
    if (signatureChanged || input.forceRefresh) {
      const availableRecent = getRecentAvailableDayKeys(
        nextDays,
        typeFilter === 'pas' ? PAS_PRELOAD_DAY_WINDOW : RECENT_DAY_WINDOW,
      );
      for (const dayKey of [...store.assetsByDay.keys()]) {
        if (input.forceRefresh || availableRecent.includes(dayKey) || store.expandedDayKeys.has(dayKey)) {
          store.assetsByDay.delete(dayKey);
        }
      }
    }
  }

  const recentDayKeys = getRecentAvailableDayKeys(
    nextDays,
    typeFilter === 'pas' ? PAS_PRELOAD_DAY_WINDOW : RECENT_DAY_WINDOW,
  );
  for (const dayKey of recentDayKeys) {
    store.expandedDayKeys.add(dayKey);
  }

  const daysToLoad = Array.from(new Set([
    ...recentDayKeys,
    ...Array.from(store.expandedDayKeys).filter((dayKey) => (
      nextDays.some((entry) => entry.dayKey === dayKey && entry.count > 0)
      || store.assetsByDay.has(dayKey)
    )),
    // 兼容旧逻辑：日历近窗若有内容也预载
    ...calendarRecentDayKeys.filter((dayKey) => nextDays.some((entry) => entry.dayKey === dayKey && entry.count > 0)),
  ])).filter((dayKey) => input.forceRefresh || !store.assetsByDay.has(dayKey));

  await Promise.all(daysToLoad.map(async (dayKey) => {
    try {
      await ensureHomeAssetDayLoaded({
        userId: input.userId,
        subject: input.subject,
        dayKey,
        forceRefresh: input.forceRefresh,
        typeFilter,
      });
    } catch {
      // Keep other days when one day fails.
    }
  }));

  if (input.supplementalAssets?.length) {
    mergeHomeStoreAssetsByIds(input.userId, input.subject, input.supplementalAssets, typeFilter);
  }

  return snapshotStore(store);
}
